/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { computed, action, set } from '@ember/object';
import { later } from '@ember/runloop';
import { reads } from '@ember/object/computed';
import { A } from '@ember/array';
import { tracked } from '@glimmer/tracking';
import { addObserver, removeObserver } from '@ember/object/observers';
import PostUtils, { postCustomInteraction, getPostTheme } from 'mewe/utils/post-utils';
import PostApi from 'mewe/api/post-api';
import DiscoverApi from 'mewe/api/discover-api';
import PS from 'mewe/utils/pubsub';
import { Theme } from 'mewe/constants';
import GroupStore from 'mewe/stores/group-store';
import { findGifUrl } from 'mewe/utils/gif-utils';
import { openPostbox, repostMediaToChat } from 'mewe/utils/dialogs-common';
import { installDropdown } from 'mewe/utils/dropdown-utils';
import { getLinkForPost } from 'mewe/utils/miscellaneous-utils';
import { FeedTypes } from 'mewe/constants';
import dispatcher from 'mewe/dispatcher';
import Mentions from 'mewe/utils/mentions-utils';
import HashtagsUtils from 'mewe/utils/hashtags-utils';
import storage from 'mewe/shared/storage';
import { isPresent } from '@ember/utils';
export default class MwPost extends Component {
  @service router;
  @service settings;
  @service purchase;
  @service account;
  @service features;
  @service dynamicDialogs;
  @service dynamicPopups;
  @service analytics;
  @reads('args.feedTheme') feedTheme;
  @reads('args.scope') scope;
  @reads('args.post') post;
  @reads('args.post.isTranslated') isTranslated;

  @tracked dropdownOptions;
  @tracked commentFieldOpened;
  @tracked mentionsStrategy;
  @tracked hashtagsStrategy;
  @tracked didOpenComments;
  @tracked truncate;

  postApi = PostApi;
  profilePopupSelector = '.post_text .h-mention';
  initialPostText = this.post.textDisplay;
  initialPostTextServer = this.post.textServer;

  constructor() {
    super(...arguments);

    if (this.args.isPostDialog && this.post.poll) {
      // if is single post dialog refresh options to show current status
      dispatcher.dispatch('poll', 'refreshVotes', this.post);
    }

    // public posts are displayed in full length and without "Show more" button
    if (this.args.post.isPublicViewPost) {
      this.setTruncate(false);
    }

    this.showProfilePopupBind = this.showProfilePopup.bind(this);

    installDropdown(this, 'dropdownOptions', {
      onUpdate: () => this.args.onCloseUpdate?.(this.post)
    });
  }

  get videoFeedAutoPlaySetting() {
    return storage.get(storage.keys.videoFeedAutoPlaySetting);
  }

  @action
  onInsert() {
    if (this.post.placeholder) return;

    // in case of group posts we need to check if user is member of that group
    // so after fetching groups list we check if group was returned from BE
    if (this.post.groupId) {
      GroupStore.getState().deferred.promise.then(() => {
        if (this.isDestroyed || this.isDestroying) return;

        const fullGroupModel = GroupStore.getGroupIfMember(this.post.groupId);
        // if user is group member then we have full group object and
        // post can be updated with that to replace SimpleGroup model
        if (fullGroupModel) {
          // groups from GET/groups response don't have 'isMember' field
          // and it's important for have that info for handling author/origin link handling
          set(fullGroupModel, 'isMember', true);

          set(this, 'post.group', fullGroupModel);
        }
      });
    }

    if (this.post.isEditing) {
      this.editingObserver();
    }
    addObserver(this, 'post.isEditing', this.editingObserver);

    this.isInserted = true;

    // TEST PERFORMANCE
    // if (window.testRunning && window.testRunning()) {
    //   if (this.post.firstPostPerformance) {
    //     next(this, () => {
    //       endMeasure('first-load');
    //     });
    //   }
    // }
    // TEST PERFORMANCE END
  }

  @action
  onDestroy() {
    if (this.isInserted) {
      removeObserver(this, 'post.isEditing', this.editingObserver);
    }
  }

  get isVerboseEnabled() {
    return localStorage.getItem('meweVerbose');
  }

  get autoPlayVideo() {
    if (!this.videoFeedAutoPlaySetting) {
      return false;
    }

    if (this.args.autoplayVideo == undefined && this.videoFeedAutoPlaySetting) {
      const { privacymail, pending, scheduled, textParts } = this.post;
      return !privacymail && !pending && !scheduled && !textParts;
    }

    return this.args.autoplayVideo;
  }

  @computed('purchase.premium.purchased')
  get hasPremium() {
    return this.purchase.get('premium.purchased'); // getter needed, otherwise throws error in Scheduled Posts list
  }

  @computed('args.page', 'post.{postedByPage,page}')
  get postPage() {
    if (this.args.page) return this.args.page;
    else if (this.post.postedByPage && this.post.page) return this.post.page;
  }

  @computed('post.{discoverScope,isGroupPreview}')
  get isGroupPostPreview() {
    return this.post.discoverScope === 'group' || this.post.isGroupPreview;
  }

  @computed('post.isPublicContent', 'isGroupPostPreview')
  get isDisabledPostInteraction() {
    return this.post?.isPublicContent || this.isGroupPostPreview;
  }

  // this is for completely disabling link and not haveing any interaction
  @computed('post.{groupId,group.isMember}')
  get isAuthorLinkDisabled() {
    // for refPosts in public view disable author link completely
    if (this.post.isPublicContent && this.args.isRefPost) return true;

    // disable group/profile link for group posts if user is not member of group
    if (this.post.groupId && !this.post.group.isMember) return true;
  }

  // for public Page and Profile we show custom interaction popup,
  // but for public post we allow click on author link to open profile
  // (except for ref posts)
  @computed('post.{isPublicContent,isPublicViewPost,isRefPost}')
  get hasAuthorLinkCustomInteraction() {
    return this.post.isPublicContent && !this.post.isPublicViewPost && !this.args.isRefPost;
  }

  get canAddEmoji() {
    // if 4 emojis are already added, don't show emoji button
    return this.post.canAddEmoji && this.post.emojisItems.length < 4;
  }

  showProfilePopup(target) {
    if (this.post?.isPublicContent) return;
    if (!this.isDestroyed && !this.isDestroying) {
      this.dynamicPopups.openPopup('mw-profile-popup', {
        parent: target,
        owner: { id: target.getAttribute('data-userid') },
        group: this.post.group,
        isMention: true,
      });
    }
  }

  // placeholderObserver: observer('post.placeholder', function () {
  //   if (!this.isInserted) return;

  //   if (this.get('post.placeholder')) return;

  //   // TEST PERFORMANCE
  //   if (window.testRunning && window.testRunning()) {
  //     if (this.get('post.firstPostPerformance')) {
  //       next(this, () => {
  //         endMeasure('first-load');
  //       });
  //     }
  //   }
  //   // TEST PERFORMANCE END
  // }),

  editingObserver() {
    if (!this.hashtagsStrategy) {
      const post = this.post;
      const mentionsScope = Mentions.getPostMentionsScope(post);
      const { scope, scopeId } = PostUtils.getPostScopeAndId(post);
      this.hashtagsStrategy = HashtagsUtils.getHashtagStrategy(scope, scopeId);
      this.mentionsStrategy = Mentions.createTextCompleteStrategy(mentionsScope, post);
    }
  }

  @computed('feedTheme', 'post.{isGroupPost,isEventPost}')
  get showUserAndCommunity() {
    // only group & event posts need this header to indicate post origin (only outside of group/event feeds)
    if (!this.post.isGroupPost && !this.post.isEventPost) return false;
    // always show group header for shared posts to know the origin of post
    if (this.args.isSharedPost) return true;
    if (this.args.isPostDialog) return true;
    return this.feedTheme !== FeedTypes.GROUP && this.feedTheme !== FeedTypes.EVENT;
  }

  @computed('feedTheme', 'post.{featured,groupId,isPublicContent}')
  get showFeaturedIndicator() {
    if (this.post.isPublicContent) return false;

    const isAllGroupsOrPages = this.feedTheme === Theme.GROUPS || this.feedTheme === Theme.PAGES;
    const isAllFeed = this.feedTheme === FeedTypes.ALL;
    const isHomeFeed = this.feedTheme === Theme.CONTACTS;
    const isGroupPostOutsideGroupFeed =
      this.feedTheme !== Theme.GROUP && this.post.groupId && this.feedTheme !== Theme.EVENT; // group event is fine

    return (
      this.post.featured &&
      !this.post.discoverScope &&
      !isAllGroupsOrPages &&
      !isHomeFeed &&
      !isAllFeed &&
      !isGroupPostOutsideGroupFeed
    );
  }

  @computed('post.textServer')
  get isGifLinkWithGifInText() {
    return (
      !!findGifUrl(this.post.link?._links?.url?.href) &&
      this.post.textServer.indexOf(this.post.link?._links?.url?.href) !== -1
    );
  }

  @computed('post.album', 'args.inMediaDialog', 'args.threadId')
  get showPostAlbumName() {
    return this.post.album && !this.args.inMediaDialog && !this.args.threadId;
  }

  @computed('post.canShare', 'account.activeUser.id')
  get showShareButton() {
    return this.post.canShare && this.account.activeUser.id;
  }

  get isSendButtonDisabled() {
    return this.post.scheduled;
  }

  @computed('post.canRepost')
  get showRepostButton() {
    return this.post.canRepost;
  }

  @computed('post.isGroupPreview')
  get showOpenCommentsButton() {
    return this.post.comments?.total > 0;
  }

  @computed('showShareButton', 'showRepostButton', 'showOpenCommentsButton')
  get hasAnyPostbarButton() {
    return this.showShareButton || this.showRepostButton || this.showOpenCommentsButton;
  }

  @computed('args.commentsOpened', 'didOpenComments')
  get commentsOpened() {
    return this.didOpenComments ?? this.args.commentsOpened;
  }

  @computed('args.{postContext,scope,isFromMyWorld}')
  get postContext() {
    const { postContext, scope, isFromMyWorld } = this.args;

    if (postContext) {
      if (postContext === 'alerts') {
        return scope + '_alerts';
      }
      return postContext;
    }

    const isOneFeedEnabled = this.features.isEnabled('web__is_one_feed_enabled');

    const feedContexts = {
      'one-feed': 'feed_all', // Following tab
      all: isOneFeedEnabled ? 'feed_following' : 'feed_all', // All tab
      contacts: 'feed_following', // Favorites tab
      discover: 'feed_discover',
      groups: 'feed_groups',
      pages: 'feed_pages',
      hashtag: 'feed_global_hashtag',
    };

    // https://sgrouples.slack.com/archives/C095A4QCKMW/p1759921339401119?thread_ts=1759473114.890609&cid=C095A4QCKMW
    // All tab with FF ON -> One feed (/onefeed) with context feed_all
    // All tab with FF OFF ->  All feed (/allfeed) with context feed_all

    // Following tab with FF ON -> All feed (/allfeed) with context feed_following
    // Following tab with FF OFF ->  Post feed (/postfeed) with context feed_following

    // Favorites tab (FF ON only) -> Favorites feed (/favorites/postsfeed) with context feed_following

    if (isFromMyWorld && feedContexts[scope]) {
      return feedContexts[scope];
    }

    const directContexts = ['groups', 'group', 'pages', 'page', 'event', 'profile'];
    if (directContexts.includes(scope)) {
      return scope;
    }

    // Default case if no conditions are met
    return undefined;
  }

  @computed(
    'post.{canFeature,canRemove,canAddToAlbum,canReport,canEdit,cannotFollow}',
    'args.hidePostbar'
  )
  get showDropdown() {
    const conditions = {
      canFeature: this.post.canFeature,
      canRemove: this.post.canRemove,
      canAddtoAlbum: this.post.canAddToAlbum,
      canReport: this.post.canReport,
      canEdit: this.post.canEdit,
      cannotFollow: !this.post.cannotFollow,
    };

    const testValues = Object.values(conditions).includes(true);

    return testValues && !this.args.hidePostbar && !this.post.isProcessingVideo;
  }

  @computed('post.{page.isFollower,discoverScope,postedByPage}', 'args.{isSharedPost,inMediaDialog}')
  get showFollowButton() {
    if (this.args.isSharedPost) return false;
    if (this.args.inMediaDialog) return false;

    return this.post.postedByPage && this.post.discoverScope && !this.post.page?.isFollower;
  }

  @computed('args.isSharedPost', 'feedTheme', 'post.{scheduled,groupId}')
  get showSharingInfo() {
    const allFeedScope = (this.feedTheme === FeedTypes.ALL || this.feedTheme === FeedTypes.HASHTAG) && !this.post.isGroupPost && !this.post.page;
    const isScheduledToContacts = this.post.scheduled && !this.post.groupId && this.feedTheme !== 'page';
    const isProperScope =
      this.feedTheme === 'contacts' || this.feedTheme === 'profile' || this.feedTheme === 'privacymail' || allFeedScope || isScheduledToContacts;

    if (!isProperScope) return false;
    if (this.args.isSharedPost) return false;
    if (this.post.isProcessingVideo) return false;

    return true;
  }

  isHashtagClicked(target) {
    const hashtagElementClicked =
      target.classList.contains('h-hashtag') ||
      (target.classList.contains('word-highlight') && target.parentNode.classList.contains('h-hashtag'));

    // when clicked on ht in shared post then click is handled twice,
    // click handled by outer post needs to be prevented
    const clickInsideSharedPost = this.post.refPost && target.closest('.shared-post');

    return hashtagElementClicked && !clickInsideSharedPost;
  }

  isVideoClicked(target) {
    return target.closest('.post_content--video');
  }

  isLinkClicked(target) {
    return target.closest('a');
  }

  isTranslationClicked(target) {
    return target.closest('.c-mw-translation');
  }

  isCustomInteractionAllowed(target) {
    // easy way to check if user is unauthenticated, check if we have his userId
    const isAuthenticated = this.account.activeUser.id;

    // authenticated user - list of elements that can be clicked
    // without triggering "Group preview" popup
    const authenticatedSelectorsAllowed = [
      '.post_header_user-wrapper',
      '.h-hashtag', // authenticated user can always click on hashtag, while unauthenticated can't (MW-3718)
      '.comment-img',
      '.post-media_wrapper'
    ];

    // unauthenticated user - list of elements that can be clicked
    // without triggering "New to Mewe?" popup
    const unauthenticatedSelectorsAllowed = [
      '.post_header_user-wrapper',
      '.comment-img',
    ];

    const selectors = isAuthenticated ? authenticatedSelectorsAllowed : unauthenticatedSelectorsAllowed;
    return selectors.some(selector => target.closest(selector));
  }

  @action
  clickHandler(e) {
    // post to share/repost, preview in postbox during sharing
    if (this.args.postInPostbox) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const target = e.toElement || e.relatedTarget || e.target;

    // `customInteraction` flag indicates that post should trigger some popup when clicked,
    // with some exceptions that are checked by `isCustomInteractionAllowed()`
    if (this.post?.customInteraction && !this.isCustomInteractionAllowed(target)) {
      e.preventDefault();
      postCustomInteraction(this.dynamicDialogs, this.post, target, { context: this.args.isRefPost ? 'shared_post' : this.postContext }, this.args.openHashTagFromTop);
      return;
    }

    if (this.args.skin == 'skin-shared') {
      if (this.post?.customInteraction) return false;
      if (this.isLinkClicked(target)) return false; // could be the post author/origin or a link in post content
      if (this.isVideoClicked(target)) return false; // video player, don't open SPD when clicked on video
      if (this.isTranslationClicked(target)) return false; //prevent from opening popup when clicked on translation
      if (this.isHashtagClicked(target)) return false;

      let s = PostUtils.getPostScopeAndId(this.post);
      let postScope = s.scope;
      let postScopeId = s.scopeId;

      let opt = {
        scope: postScope,
        scopeId: postScopeId,
        postItemId: this.post.postItemId,
        threadId: this.post.threadId, // used in case of PP results in search
        fetchingPostForDialog: true,
        doWithPost: (thePost) => {
          this.dynamicDialogs.openDialog('single-post-dialog', {
            post: thePost,
            theme: postScope,
            scope: postScope,
            isRefPost: true,
            allowMultipleInstances: true,
          });
        },
      };

      if (this.post.page) opt.pageId = this.post.page.id;
      if (this.post.groupId) opt.groupId = this.post.groupId;

      dispatcher.dispatch('feed', 'fetchPostFromMemoryOrServer', opt);

      return false;
    }

    if (this.isHashtagClicked(target)) {
      if (this.post.isEditing) return false;
      if (this.post.isScheduled) return false;
      // hashtags are not supported in MC feed
      if (this.scope === Theme.MYCLOUD) return;
      // opened in new tab
      if (e.ctrlKey) return;

      if (this.args.isRefPost && this.post.groupId) {
        const confirmedGroup = GroupStore.getGroupIfMember(this.post.group.id);

        if (!confirmedGroup) {
          window.location.pathname = 'group/' + this.post.groupId;
          return false;
        }
      }

      const htEl = target.parentNode.classList.contains('h-hashtag') ? target.parentNode : target;
      // hashtags are not supported in comments
      if (htEl.closest('.comment_text')) return;

      if (htEl.closest('.chat-info-media') && this.post.group.id) {
        PS.Pub('chat.minimize', this.post.group.id);
      }

      const innerHTML = htEl.innerHTML.indexOf('word-highlight') > -1 ? htEl.innerText : htEl.innerHTML;
      const tag = innerHTML.trim().replace('#', '').toLowerCase();

      document.documentElement.scrollTop = 0; // scroll to top of feed when opening hashtag feed
      const scope = this.post.discoverScope || this.post.isGroupPreview ? FeedTypes.HASHTAG : this.post.pageId || this.post.eventId || this.post.group.id;

      // openHashTagFromTop - callback passed from top (route or some other parent) to the post,
      // in order to keep the context of feed when opening hashtag feed. If it's missing then go to fallback
      if (this.args.openHashTagFromTop) {
        this.args.openHashTagFromTop(tag, scope, this.post);
        e.preventDefault();
      }
      // openHashTagFromPost - callback passed from post down to child components in order to open
      // openHashTagFromTop callback of post that opened the media dialog
      // (use case: open photo-post with hasthag in /groups feed, then click on hashtag in MD -> /groups?tag= should be opened)
      else if (this.args.openHashTagFromPost) {
        this.args.openHashTagFromPost(e, tag);
      } else {
        this.openHashTagFallback(tag);
      }

      this.dynamicDialogs.closeAll();
    }
  }

  @action
  openHashTagFromPost(e, tag) {
    if (this.args.openHashTagFromTop) {
      this.clickHandler(e);
    } else {
      this.openHashTagFallback(tag);
    }
  }

  openHashTagFallback(tag) {
    let groupId,
      params = A();

    let href = 'app.myworld.index';
    if (this.feedTheme === Theme.PROFILE && this.scope === Theme.PROFILE) {
      href = 'app.publicid.posts';
      params.push(this.post.owner.publicLinkId);
    } else if (this.post.eventId) {
      href = 'app.event.feed';
      params.push(this.post.eventId);
    } else if (this.post.pageId) {
      href = 'app.publicid.feed';
      params.push(this.post.page?.publicLinkId);
    } else if (this.post.group) {
      groupId = this.post.group.id;

      if (groupId !== Theme.PRIVATEPOSTS && groupId !== Theme.CONTACTS && this.args.postContext !== 'group_preview') {
        href = 'app.group.index.feed';
        params.push(groupId);
      }
    }

    if (href) {
      let query = {
        tag: tag.replace('#', '').toLowerCase(),
      };

      // set scope to All posts when redirecting to myworld feed to get global hashtags
      if (href === 'app.myworld.index') {
        query.scope = 'all';
      }

      if (params.length) this.router.transitionTo(href, ...params, { queryParams: query });
      else this.router.transitionTo(href, { queryParams: query });
    }
  }

  @computed('post.pending', 'args.hidePostbar')
  get postbarVisible() {
    if (this.post.isProcessingVideo) {
      return false;
    }
    return this.args.postbarVisible ?? (!this.post.pending && !this.args.hidePostbar);
  }

  @computed('post.{isGroupPreview,options.noComments}')
  get showCommentsTable() {
    return !this.post.options?.noComments && !this.post.isGroupPreview;
  }

  @computed('post.{commentsDisabled,isPublicContent}', 'args.{isRefPost}')
  get displayCommentsDisabledText() {
    // don't show this info in public post view
    if (this.post.isPublicContent) return false;
    // don't show this info in shared post
    if (this.args.isRefPost) return false;

    return this.post.commentsDisabled;
  }

  @computed('post.isTranslated', 'account.temporaryLanguage', 'account.activeUser.supportedLanguages')
  get displayTranslationFeature() {
    const isLangDifferent = this.account.currentTranslationLanguage !== this.post.language && isPresent(this.account.activeUser.supportedLanguages);
    const canTranslate =
      this.post.language &&
      isLangDifferent &&
      this.features.isEnabled('translationsPosts') &&
      this.post.textDisplay &&
      this.hasMinimumRequiredChars &&
      !this.post.isCurrentUserPostOwner;

    if (this.post.poll) {
      return (
        this.post.isTranslated ||
        (this.post.language &&
          isLangDifferent &&
          this.features.isEnabled('translationsPolls') &&
          this.hasMinimumRequiredChars &&
          !this.post.isCurrentUserPostOwner)
      );
    }

    return this.post.isTranslated || canTranslate;
  }

  get hasMinimumRequiredChars() {
    let pollQuestionLength = 0;
    let pollOptionsLength = 0;
    const textLength = this.post.textServer?.length;

    if (this.post.poll) {
      pollQuestionLength = this.post.poll.question?.length || 0;
      this.post.poll.options?.forEach((option) => (pollOptionsLength += option.text?.length));
    }

    return this.account.minimumNumberOfCharacters ? textLength + pollQuestionLength + pollOptionsLength > this.account.minimumNumberOfCharacters : true;
  }

  @action
  handleTranslations(translation) {
    if (translation && !translation.errorCode) {
      set(this.args.post, 'isTranslated', translation.isTranslated);
      set(this.args.post, 'textServer', translation.data.text);

      if (this.post.poll) {
        if (translation.isTranslated) {
          this.post.poll.options?.forEach((option, index) => {
            set(option, 'textTranslated', translation.data.options[index]?.textTranslated);
          });
        }

        set(this, 'post.poll.question', translation.data.question);
      }
    }
  }

  @action
  openAlbumMediaDialog() {
    if (!this.post.canOpenAlbum) return;

    if (this.post?.customInteraction) {
      return;
    }

    const post = this.post;
    // shared post should be opened in SPV and there user can open album
    if (this.args.isSharedPost) return;

    let scope = post.scope || this.scope || this.feedTheme;

    if (scope == FeedTypes.ALL) {
      scope = PostUtils.getPostScopeAndId(post).scope;
    }

    this.dynamicDialogs.openDialog('media-dialog', {
      galleryView: true,
      mediaType: 'album',
      post: post,
      groupId: post.group.id,
      eventId: post.eventId || post.eventInfo?.id,
      userId: post.owner?.id, // owner undefined for e.g. page posts
      pageId: post.pageId,
      album: post.album,
      scope: scope,
      isMyCloud: this.feedTheme === Theme.MYCLOUD,
      isCurrentUserPostOwner: post.isCurrentUserPostOwner,
      allowMultipleInstances: true,
    });
  }

  @action
  shareTo(target) {
    this.hideTranslations();

    if (target === 'chat') {
      repostMediaToChat({ message: getLinkForPost(this.post) }, this.dynamicDialogs);
    } else {
      openPostbox(
        {
          target: target,
          postToShare: this.post.refPost ? this.post.refPost : this.post,
          theme: getPostTheme(this.post, this.feedTheme),
          ppOrigin: 'share-post',
        },
        this.dynamicDialogs
      );
    }
  }

  @action
  repostTo(target) {
    this.hideTranslations();

    if (target === 'chat') {
      repostMediaToChat({ message: getLinkForPost(this.post) }, this.dynamicDialogs);
    } else {
      openPostbox(
        {
          target: target,
          postToRepost: this.post,
          theme: getPostTheme(this.post, this.feedTheme),
          ppOrigin: 'repost-post',
        },
        this.dynamicDialogs
      );
    }
  }

  hideTranslations() {
    if (this.isTranslated) {
      set(this.args.post, 'textServer', this.initialPostTextServer);
      set(this.args.post, 'isTranslated', false);
    }
  }

  @action
  unhidePost() {
    if (this.post.temporarilyHidden === 'singlePost') {
      DiscoverApi.unignoreContent('post', this.post.id).then(() => {
        set(this, 'post.temporarilyHidden', false);
      });
    } else {
      const ignoreType = this.post.postedByPage ? 'page' : 'group';
      const ignoreId = this.post.postedByPage ? this.post.pageId : this.post.groupId;

      DiscoverApi.unignoreContent(ignoreType, ignoreId).then(() => {
        const posts = this.args.collection.posts.filter((p) => {
          if (ignoreType === 'group') {
            return p.groupId === ignoreId;
          } else {
            return p.pageId === ignoreId;
          }
        });
        posts.forEach((p) => set(p, 'temporarilyHidden', false));
      });
    }
  }

  @action
  toggleComments() {
    if (this.post.isGroupPreview) return;
    this.didOpenComments = !this.didOpenComments;
  }

  @action
  followPage() {
    dispatcher.dispatch('pages', 'toggleFollow', this.post.page, { setToFollow: true, isActionFromDiscover: true });
  }

  @action
  setFeatured(doFeature) {
    set(this, 'post.featured', doFeature);
  }

  @action
  setTruncate(value) {
    this.truncate = value;
  }

  @action
  openCommentField() {
    this.commentFieldOpened = true;
  }
}
